import React, { useState } from 'react';
import { Button, Icon, Modal, Table } from '@syneto/compass-react';
import Preloader from '../../../../components/preloader';
import {
	EMPTY_FLEET_FILTERS,
	FLEET_COMPONENTS_SIZE_PER_PAGE_LIST,
	NOT_AVAILABLE
} from '../../../../constants/hardware-inventory';
import FleetComponentDetails from './fleetComponentDetails';
import FleetInventoryFilters from './fleetInventoryFilters';
import { findComponentTypeFor } from './hardwareInventoryMapper';

const FleetInventoryTable = (props) => {
	const {
		components,
		pagination,
		isLoading,
		hasError,
		componentTypes,
		filters,
		smartQuery,
		smartSearch,
		selectedComponentType,
		onFilterChange,
		onSmartQueryChange,
		onClearAll,
		onPaginationChange
	} = props;

	const [component, setComponent] = useState(null);
	const [showDetailsModal, toggleDetailsModal] = useState(false);

	const openDetailsModal = (component) => {
		setComponent(component);
		toggleDetailsModal(true);
	};

	const closeDetailsModal = () => {
		setComponent(null);
		toggleDetailsModal(false);
	};

	/*
	 * Only pagination comes back through the table now - searching and filtering live in the filter
	 * bar above it, because the endpoint has no free-text parameter for the built-in search box to
	 * drive.
	 */
	const handleTableChange = (type, { page, sizePerPage }) => {
		if (type !== 'pagination') {
			return;
		}

		const start = (page - 1) * sizePerPage;

		onPaginationChange(start >= 0 ? start : 0, sizePerPage);
	};

	const renderValue = (cell) => {
		return cell ?? NOT_AVAILABLE;
	};

	const hasActiveFilters = Object.keys(EMPTY_FLEET_FILTERS).some((field) => {
		return Boolean(filters[field]);
	}) || Boolean(smartQuery);

	/*
	 * A failed fetch leaves no rows, so the messages below would state a result the request never
	 * returned - an empty fleet, or a filter that matched nothing. The alert above carries the
	 * reason; here we only decline to draw a conclusion.
	 */
	const emptyTableState = () => {
		if (isLoading) {
			return <Preloader dataIsLoading={true} />;
		}

		if (hasError) {
			return 'Fleet inventory could not be loaded.';
		}

		return hasActiveFilters
			? 'No components match the current filters.'
			: 'No hardware components have been reported yet.';
	};

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-2">
				<h6 className="mb-0">Fleet inventory</h6>

				{selectedComponentType && (
					<Button role="tertiary" onClick={onClearAll}>
						{selectedComponentType.displayName}
						<Icon name="fa fa-times ms-2" />
					</Button>
				)}
			</div>

			<FleetInventoryFilters
				filters={filters}
				smartQuery={smartQuery}
				smartSearch={smartSearch}
				hasActiveFilters={hasActiveFilters}
				onSmartQueryChange={onSmartQueryChange}
				onFilterChange={onFilterChange}
				onClearAll={onClearAll}
			/>

			<Table
				data={components ?? []}
				keyField="id"
				remote
				pagination={{
					sizePerPageList: FLEET_COMPONENTS_SIZE_PER_PAGE_LIST,
					sizePerPage: pagination?.itemsPerPage,
					showTotal: true,
					page: pagination?.currentPage,
					totalSize: pagination?.totalItems
				}}
				rowEvents={{ onClick: (e, row) => { return openDetailsModal(row); } }}
				onTableChange={handleTableChange}
				noDataIndication={emptyTableState()}
			>
				<Table.Col field="applianceSerialNumber" align="left" renderCell={renderValue}>
					Appliance serial number
				</Table.Col>
				<Table.Col field="category" align="left" renderCell={renderValue}>Category</Table.Col>
				<Table.Col field="displayName" align="left" renderCell={renderValue}>Component</Table.Col>
				<Table.Col field="firmwareVersion" align="left" renderCell={renderValue}>Firmware</Table.Col>
				<Table.Col field="location" align="left" renderCell={renderValue}>Location</Table.Col>
			</Table>

			<Modal show={showDetailsModal} size="lg" onHide={closeDetailsModal}>
				<Modal.Header>
					<Modal.Title>Hardware component details</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					{component && (
						<FleetComponentDetails
							component={component}
							componentType={findComponentTypeFor(component, componentTypes)}
						/>
					)}
				</Modal.Body>

				<Modal.Footer>
					<Button onClick={closeDetailsModal}>Close</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default FleetInventoryTable;
